import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";

const ASPECT_CLASS = "-aspect-ready";

type Dims = { width: number; height: number };

interface LazyImageState {
  src: string;
  aspectReady: boolean;
  contentReady: boolean;
  aspectLoading: boolean;
  contentLoading: boolean;
  pendingActivation: boolean;
  width?: number;
  height?: number;
  aspectPromise?: Promise<void>;
  unsubscribe?: () => void;
  rangeAttempted: boolean;
  blobUrl?: string;
  controller?: AbortController;

  fetching: boolean;
  allowSrcFallback?: boolean;
}

function parsePng(b: ArrayBuffer): Dims {
  const d = new DataView(b);
  if (b.byteLength < 24) return { width: 0, height: 0 };
  if (d.getUint32(0) !== 0x89504e47 || d.getUint32(4) !== 0x0d0a1a0a)
    return { width: 0, height: 0 };
  if (d.getUint32(12) !== 13 || d.getUint32(16) !== 0x49484452) return { width: 0, height: 0 };
  return { width: d.getUint32(20, false), height: d.getUint32(24, false) };
}

function parseJpeg(b: ArrayBuffer): Dims {
  const d = new DataView(b);
  if (d.getUint16(0) !== 0xffd8) return { width: 0, height: 0 };
  let o = 2;
  while (o + 9 < b.byteLength) {
    const m = d.getUint16(o);
    o += 2;
    if (m === 0xffda || m === 0xffd9) break;
    const len = d.getUint16(o);
    if (len < 2 || o + len > b.byteLength) break;
    if (
      (m >= 0xffc0 && m <= 0xffc3) ||
      (m >= 0xffc5 && m <= 0xffc7) ||
      (m >= 0xffc9 && m <= 0xffcb) ||
      (m >= 0xffcd && m <= 0xffcf)
    ) {
      return { height: d.getUint16(o + 3), width: d.getUint16(o + 5) };
    }
    o += len;
  }
  return { width: 0, height: 0 };
}

function parseWebp(b: ArrayBuffer): Dims {
  const d = new DataView(b);
  if (b.byteLength < 16) return { width: 0, height: 0 };
  if (d.getUint32(0, true) !== 0x46464952 || d.getUint32(8, true) !== 0x50424557)
    return { width: 0, height: 0 };
  let i = 12;
  while (i + 8 <= b.byteLength) {
    const fourcc = d.getUint32(i, false);
    const size = d.getUint32(i + 4, true);
    const s = i + 8;
    if (fourcc === 0x58503856 && size >= 10 && s + size <= b.byteLength) {
      const w = (d.getUint16(s + 4, true) | (d.getUint8(s + 6) << 16)) + 1;
      const h = (d.getUint16(s + 7, true) | (d.getUint8(s + 9) << 16)) + 1;
      return { width: w, height: h };
    }
    i = s + size + (size & 1);
  }
  return { width: 0, height: 0 };
}

function tryParseDims(buffer: ArrayBuffer, ct?: string | null): Dims {
  const c = (ct || "").toLowerCase();
  if (c.includes("png")) return parsePng(buffer);
  if (c.includes("jpeg") || c.includes("jpg")) return parseJpeg(buffer);
  if (c.includes("webp")) return parseWebp(buffer);
  let d = parsePng(buffer);
  if (d.width) return d;
  d = parseJpeg(buffer);
  if (d.width) return d;
  d = parseWebp(buffer);
  if (d.width) return d;
  return { width: 0, height: 0 };
}

async function fetchImageStreamWithDims(
  src: string,
  opts?: {
    credentials?: RequestCredentials;
    referrerPolicy?: ReferrerPolicy;
    signal?: AbortSignal;
  },
  onDims?: (d: Dims) => void
): Promise<{
  dims: { width: number; height: number } | null;
  blobUrl: string;
  contentType?: string | null;
}> {
  const res = await fetch(src, {
    mode: "cors",
    credentials: opts?.credentials ?? "omit",
    referrerPolicy: opts?.referrerPolicy,
    signal: opts?.signal,
    cache: "default",
  });
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

  const ct = res.headers.get("content-type");
  const reader = res.body.getReader();

  const PROBE_CAP = 1048576;
  const TRY_STEP = 4096;

  const probe = new Uint8Array(PROBE_CAP);
  let probeLen = 0;
  let lastTriedLen = 0;

  const chunks: ArrayBuffer[] = [];
  let dims: Dims | null = null;
  let announced = false;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;

    const part = value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength);
    chunks.push(part);

    if (!dims && probeLen < PROBE_CAP) {
      const toCopy = Math.min(value.byteLength, PROBE_CAP - probeLen);
      if (toCopy > 0) {
        probe.set(value.subarray(0, toCopy), probeLen);
        probeLen += toCopy;
      }
      if (probeLen - lastTriedLen >= TRY_STEP) {
        const view = probeLen === probe.byteLength ? probe : probe.slice(0, probeLen);
        const d = tryParseDims(view.buffer, ct);
        if (d.width && d.height) {
          dims = d;
          if (!announced && onDims) {
            onDims(dims);
            announced = true;
          }
        }
        lastTriedLen = probeLen;
      }
    }
  }

  if (!dims) {
    const full = await new Response(new Blob(chunks)).arrayBuffer();
    const d = tryParseDims(full, ct);
    if (d.width && d.height) {
      dims = d;
      if (!announced && onDims) {
        onDims(dims);
        announced = true;
      }
    }
  }

  const blob = new Blob(chunks, { type: ct || "application/octet-stream" });
  const blobUrl = URL.createObjectURL(blob);
  return { dims, blobUrl, contentType: ct };
}

export class StringLazy extends StringModule {
  private isStartLoaded = false;
  private loadingCount = 0;
  private imageStates = new WeakMap<HTMLImageElement, LazyImageState>();

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "lazy";
  }

  onInit(): void {
    const images = document.querySelectorAll("img[string-lazy], img[data-string-lazy]");
    images.forEach((img) => this.ensureState(img as HTMLImageElement));
    this.isStartLoaded = true;
  }

  onObjectConnected(object: StringObject): void {
    const element = object.htmlElement;
    if (!(element instanceof HTMLImageElement)) return;
    const state = this.ensureState(element);

    if (!state.aspectReady && !state.aspectLoading) {
      this.prepareAspectRatio(element);
    }

    const listener = (data: { inView: boolean } | undefined) => {
      this.handleInView(element, state, !!data?.inView);
    };
    if (state.unsubscribe) state.unsubscribe();
    this.events.on(`object:inview:${object.id}`, listener);
    state.unsubscribe = () => this.events.off(`object:inview:${object.id}`, listener);
    if (this.isStartLoaded && (object.getProperty<boolean>("is-inview") ?? false)) {
      this.handleInView(element, state, true);
    }
  }

  onObjectDisconnected(object: StringObject): void {
    const element = object.htmlElement;
    if (!(element instanceof HTMLImageElement)) return;
    const state = this.imageStates.get(element);
    if (!state) return;
    state.pendingActivation = false;
    if (state.controller) state.controller.abort();
    if (state.blobUrl) URL.revokeObjectURL(state.blobUrl);
    if (state.unsubscribe) {
      state.unsubscribe();
      state.unsubscribe = undefined;
    }
  }

  private ensureState(img: HTMLImageElement): LazyImageState {
    let state = this.imageStates.get(img);
    if (!state) {
      const src = this.readSource(img);
      state = {
        src,
        aspectReady: false,
        contentReady: false,
        aspectLoading: false,
        contentLoading: false,
        pendingActivation: false,
        rangeAttempted: false,
        fetching: false,
      };
      this.imageStates.set(img, state);
      if (!img.classList.contains("lazyLoad")) img.classList.add("lazyLoad");
      if (img.dataset && !img.dataset.stringLazySrc && src) img.dataset.stringLazySrc = src;
      return state;
    }
    if (!state.src) state.src = this.readSource(img);
    return state;
  }

  private readSource(img: HTMLImageElement): string {
    const raw = this.tools.domAttribute.process({ element: img, key: this.htmlKey, fallback: "" });
    if (typeof raw === "string") return raw;
    if (raw == null) return "";
    return String(raw);
  }

  private handleInView(img: HTMLImageElement, state: LazyImageState, inView: boolean): void {
    state.pendingActivation = inView;
    if (!inView) return;
    if (state.aspectReady) {
      this.maybeActivateImage(img, state);
    } else if (!state.aspectLoading) {
      this.prepareAspectRatio(img);
    }
  }

  private async prepareAspectRatio(img: HTMLImageElement): Promise<void> {
    const state = this.ensureState(img);
    if (!state.src || state.aspectLoading || state.aspectReady) return;

    state.aspectLoading = true;
    state.fetching = true;

    const crossOrigin = img.getAttribute("crossorigin");
    const referrerPolicy = img.getAttribute("referrerpolicy") as ReferrerPolicy | null;
    const ac = new AbortController();
    state.controller = ac;

    try {
      const { blobUrl } = await fetchImageStreamWithDims(
        state.src,
        {
          credentials: crossOrigin === "use-credentials" ? "include" : "omit",
          referrerPolicy: referrerPolicy || undefined,
          signal: ac.signal,
        },

        (d) => {
          if (d.width > 0 && d.height > 0 && !state.aspectReady) {
            img.style.aspectRatio = `${d.width} / ${d.height}`;
            img.classList.add(ASPECT_CLASS);
            state.width = d.width;
            state.height = d.height;
            state.aspectReady = true;
          }
        }
      );

      state.blobUrl = blobUrl;

      if (!state.aspectReady && state.width && state.height) {
        img.style.aspectRatio = `${state.width} / state.height`;
        img.classList.add(ASPECT_CLASS);
        state.aspectReady = true;
      }
    } catch {
      state.allowSrcFallback = true;
      state.aspectReady = true;
    } finally {
      state.fetching = false;
      state.aspectLoading = false;

      this.maybeActivateImage(img, state);
    }
  }

  private maybeActivateImage(img: HTMLImageElement, state: LazyImageState): void {
    if (!state.pendingActivation || state.contentReady || state.contentLoading) return;
    if (!state.aspectReady || !state.src) return;

    if (state.fetching && !state.blobUrl) return;

    if (state.blobUrl || state.allowSrcFallback) {
      this.activateImage(img, state);
    }
  }

  private activateImage(img: HTMLImageElement, state: LazyImageState): void {
    state.contentLoading = true;
    this.loadingCount++;
    const finalize = (loaded: boolean) => {
      if (!state.contentLoading) return;
      state.contentLoading = false;
      state.pendingActivation = false;
      this.loadingCount = Math.max(0, this.loadingCount - 1);
      if (loaded) {
        state.contentReady = true;
        img.classList.add("-loaded");
      }
      if (this.loadingCount === 0) this.events.emit("image:load:all", null);
    };
    const onLoad = () => finalize(true);
    const onError = () => finalize(false);
    img.addEventListener("load", onLoad, { once: true });
    img.addEventListener("error", onError, { once: true });
    img.decoding = "async";
    img.loading = img.loading || "lazy";

    if (state.blobUrl) {
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");
      img.src = state.blobUrl;
    } else {
      img.src = state.src;
    }

    if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      img.removeEventListener("load", onLoad);
      img.removeEventListener("error", onError);
      finalize(true);
    }
  }
}
